import React, { useEffect, useState, useMemo, useCallback } from "react";
import "./MarketDataStaleness.css";
import { marketDataStalenessAPI } from "@/services/api";
import { RefreshCw } from "lucide-react";
import shared from '@/components/utils/shared.module.css';
import { Button, Note, Pill, StatGrid, loadingStats } from '@/components/ui';
import type { PillTone } from '@/components/ui';
import DataTable, { ColumnDef } from '@/components/utils/DataTable';

type StalenessData = {
  id: number;
  symbol: string | null;
  name: string | null;
  ltp: number | null;
  last_updated: string | null;
  staleness_category: "Live" | "Recent" | "Aging" | "Stale" | "Critical";
};

type MarketDataStalenessResponse = {
  counts: Record<"Live" | "Recent" | "Aging" | "Stale" | "Critical", number>;
  data: StalenessData[];
};

const categoryDetails = {
  "All Active": { description: "All non-critical data" },
  Live: { description: "< 1 min ago" },
  Recent: { description: "1 - 5 mins ago" },
  Aging: { description: "5 - 15 mins ago" },
  Stale: { description: "15 - 60 mins ago" },
  Critical: { description: "> 60 mins ago or N/A" },
};

type CategoryKey = keyof typeof categoryDetails;

/* Freshness is state, so it reads as a Pill: fresh is good, then caution, then loss. */
const STATUS_TONE: Record<StalenessData["staleness_category"], PillTone> = {
  Live: 'positive',
  Recent: 'info',
  Aging: 'warning',
  Stale: 'critical',
  Critical: 'critical',
};

const MarketDataStaleness = () => {
  const [data, setData] = useState<MarketDataStalenessResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey | null>(null);
  const [sortBy, setSortBy] = useState("last_updated");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(0); // TablePagination is 0-based
  const [rowsPerPage, setRowsPerPage] = useState(50);

  const stalenessOrder = useMemo(() => ({
    Live: 1, Recent: 2, Aging: 3, Stale: 4, Critical: 5,
  }), []);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await marketDataStalenessAPI.getMarketDataStaleness();
      if (!response?.counts || !Array.isArray(response?.data)) {
        throw new Error("Invalid response format from API");
      }
      setData(response);
    } catch (err: any) {
      setError(err?.message || "Failed to fetch market data staleness.");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, selectedCategory]);

  const handleRefresh = useCallback(() => fetchData(), [fetchData]);

  const handleSort = useCallback((field: string) => {
    const newSortDir = sortBy === field && sortDir === "asc" ? "desc" : "asc";
    setSortBy(field);
    setSortDir(newSortDir);
  }, [sortBy, sortDir]);

  const handlePageChange = useCallback((_: unknown, newPage: number) => {
    setCurrentPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setCurrentPage(0);
    },
    []
  );

  const filteredData = useMemo(() => {
    if (!data?.data) return [];
    let filtered = [...data.data];
    if (selectedCategory) {
      filtered = filtered.filter((item) => item.staleness_category === selectedCategory);
    } else {
      filtered = filtered.filter((item) => item.staleness_category !== "Critical");
    }
    if (searchTerm) {
      const lowercasedFilter = searchTerm.toLowerCase();
      filtered = filtered.filter((item) =>
        item.name?.toLowerCase().includes(lowercasedFilter) ||
        item.symbol?.toLowerCase().includes(lowercasedFilter)
      );
    }
    if (sortBy) {
      filtered.sort((a, b) => {
        const aVal = a[sortBy as keyof StalenessData];
        const bVal = b[sortBy as keyof StalenessData];
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        let comparison = 0;
        if (sortBy === "staleness_category") {
          comparison = (stalenessOrder[aVal as keyof typeof stalenessOrder] || 5) -
                       (stalenessOrder[bVal as keyof typeof stalenessOrder] || 5);
        } else if (typeof aVal === "string" && typeof bVal === "string") {
          comparison = aVal.localeCompare(bVal);
        } else if (typeof aVal === "number" && typeof bVal === "number") {
          comparison = aVal - bVal;
        }
        return sortDir === "asc" ? comparison : -comparison;
      });
    }
    return filtered;
  }, [data, searchTerm, selectedCategory, sortBy, sortDir, stalenessOrder]);

  const paginatedData = useMemo(() => {
    const startIndex = currentPage * rowsPerPage;
    return filteredData.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredData, currentPage, rowsPerPage]);

  const summaryCounts = useMemo(() => (
    data?.counts || { Live: 0, Recent: 0, Aging: 0, Stale: 0, Critical: 0 }
  ), [data]);

  const getTimeAgo = (lastUpdated: string | null) => {
    if (!lastUpdated) return "No updates";
    const diffMs = new Date().getTime() - new Date(lastUpdated).getTime();
    const minutesAgo = Math.floor(diffMs / 60000);
    if (minutesAgo < 1) return "Just now";
    if (minutesAgo < 60) return `${minutesAgo}m ago`;
    const hoursAgo = Math.floor(minutesAgo / 60);
    return hoursAgo < 24 ? `${hoursAgo}h ago` : `${Math.floor(hoursAgo / 24)}d ago`;
  };

  /* Columns replace the hand-written table, so the head, sort arrows, empty
     state, footer count and pager all come from DataTable. */
  const columns = useMemo<ColumnDef<StalenessData>[]>(() => [
    { key: 'id', header: 'ID', sortable: true, width: '90px', render: r => r.id },
    { key: 'symbol', header: 'Symbol', sortable: true, width: '140px',
      cellClassName: () => shared['cell-strong'], render: r => r.symbol ?? '—' },
    { key: 'name', header: 'Company Name', sortable: true, width: '260px', render: r => r.name ?? '—' },
    { key: 'ltp', header: 'LTP', sortable: true, width: '130px', align: 'right',
      render: r => r.ltp?.toFixed(2) ?? '—' },
    { key: 'last_updated', header: 'Last Updated', sortable: true, width: '200px',
      render: r => (r.last_updated ? new Date(r.last_updated).toLocaleString() : '—') },
    { key: 'staleness_category', header: 'Data Status', sortable: true, width: '220px',
      render: r => (
        <Pill tone={STATUS_TONE[r.staleness_category]} solid={r.staleness_category === 'Critical'}>
          {r.staleness_category}
          <span className="time-ago">{getTimeAgo(r.last_updated)}</span>
        </Pill>
      ) },
  ], []);

  return (
    <DataTable<StalenessData>
      title="Market Data Staleness"
      subtitle="Companies whose price or corporate-action data has stopped updating."
      headerActions={
        <Button variant="primary" onClick={handleRefresh} disabled={loading}>
          <RefreshCw size={15} className={loading ? "spinning" : ""} />
          {loading ? "Refreshing…" : "Refresh"}
        </Button>
      }
      toolbarSlot={
        <>
          {error && <Note tone="error">{error}</Note>}
          {loading && !data && (
            <StatGrid ariaLabel="Data status" items={loadingStats(Object.keys(categoryDetails))} />
          )}
          {data && (
            <StatGrid
              ariaLabel="Data status"
              items={[
                {
                  id: 'All Active',
                  label: 'All Active',
                  value: Object.values(summaryCounts).reduce((a, b) => a + b, 0) -
                    (summaryCounts.Critical || 0),
                  hint: categoryDetails["All Active"].description,
                  title: categoryDetails["All Active"].description,
                  onClick: () => setSelectedCategory(null),
                  active: !selectedCategory,
                },
                ...(Object.entries(summaryCounts) as [CategoryKey, number][]).map(([category, count]) => ({
                  id: category,
                  label: category,
                  value: count,
                  hint: categoryDetails[category]?.description,
                  title: `Data updated ${categoryDetails[category]?.description}`,
                  onClick: () => setSelectedCategory(category),
                  active: selectedCategory === category,
                })),
              ]}
            />
          )}
        </>
      }
      data={paginatedData}
      columns={columns}
      rowKey={row => row.id}
      totalCount={filteredData.length}
      loading={loading && !data}
      currentPage={currentPage + 1}
      totalPages={Math.max(Math.ceil(filteredData.length / rowsPerPage), 1)}
      onPageChange={next => setCurrentPage(next - 1)}
      pageSize={rowsPerPage}
      pageSizeOptions={[10, 25, 50, 100]}
      onPageSizeChange={size => { setRowsPerPage(size); setCurrentPage(0); }}
      sortField={sortBy}
      sortDir={sortDir}
      onSort={field => handleSort(field)}
      searchQuery={searchTerm}
      onSearchChange={value => { setSearchTerm(value); setCurrentPage(0); }}
      searchPlaceholder="Search by company name or symbol…"
      loadingMessage="Loading market data staleness…"
      emptyMessage="No results. Try adjusting the search or the category."
    />
  );
};

export default MarketDataStaleness;
